import { Check, X, Sparkles, Gift } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { trpc } from '@/config/trpc'
import { TRIAL_DURATION_DAYS } from '@backend/lib/config/pricing'

const FREE_LIMITS = [
  '5 Posts / month',
  '25 Image Generations / month',
  '100 Chat Messages / month',
  '1 Brand Profile',
  '1 Moodboard',
  'No Automations',
]

const PRO_TRIAL_FEATURES = [
  'Unlimited Posts',
  '500 Image Generations',
  'Unlimited Chat Messages',
  '10 Brand Profiles',
  'Unlimited Moodboards',
  'Automations, API + MCP',
]

interface Props {
  open: boolean
  onClose: () => void
}

export function ClaimTrialModal({ open, onClose }: Props) {
  const utils = trpc.useUtils()
  const claimTrial = trpc.userSubscription.claimTrial.useMutation({
    onSuccess: () => {
      utils.userSubscription.get.invalidate()
      utils.usageLimit.get.invalidate()
      onClose()
    },
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-500" />
            Try Pro free for {TRIAL_DURATION_DAYS} days
          </DialogTitle>
          <DialogDescription>
            Try the full Pro plan for {TRIAL_DURATION_DAYS} days, no credit card required.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-3">
            <p className="text-sm font-medium">Free</p>
            <ul className="space-y-2">
              {FREE_LIMITS.map((feature) => (
                <li key={feature} className="text-muted-foreground flex items-center gap-2 text-sm">
                  <X className="h-3.5 w-3.5 shrink-0 text-red-400" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-3">
            <p className="text-sm font-medium">Pro Trial</p>
            <ul className="space-y-2">
              {PRO_TRIAL_FEATURES.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm">
                  <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Maybe Later
          </Button>
          <Button onClick={() => claimTrial.mutate()} disabled={claimTrial.isPending}>
            <Sparkles className="mr-2 h-4 w-4" />
            {claimTrial.isPending ? 'Activating...' : 'Start Free Trial'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
